import { TemplateFormState, LetterFormState, INITIAL_TEMPLATE_STATE, INITIAL_LETTER_STATE, generateShortId } from "../types/letterhead.types.ts";

export const safeJSON = <T>(val: string | undefined, fallback: T): T => {
  try { return val ? JSON.parse(val) : fallback; } catch { return fallback; }
};

export const dataURLtoFile = (dataurl: string, filename: string) => {
  if (!dataurl) return null;
  const arr = dataurl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) return null;
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

// Map DB Template to Form State
export const parseTemplateToState = (template: any, backendUrl: string): TemplateFormState => {
  const getIdentityValue = (key: string, fallback = "") => {
    const field = template.TemplateIdentities?.find((f: any) => f.field_key === key || f.key === key);
    return field?.field_value || field?.value || fallback;
  };

  const getProps = (comp: any) => {
    if (!comp?.TemplateProperties) return {};
    if (Array.isArray(comp.TemplateProperties)) {
      return comp.TemplateProperties.reduce((acc: any, curr: any) => {
        acc[curr.property_key] = curr.property_value; return acc;
      }, {});
    }
    return comp.TemplateProperties;
  };

  const headerProps = getProps(template.TemplateComponents.find((c: any) => c.component_type === "header"));
  const bodyProps = getProps(template.TemplateComponents.find((c: any) => c.component_type === "body"));
  const footerProps = getProps(template.TemplateComponents.find((c: any) => c.component_type === "footer"));

  let dbLogoUrl = headerProps.logoUrl || "";
  if (dbLogoUrl && !dbLogoUrl.startsWith("http") && !dbLogoUrl.startsWith("data:")) dbLogoUrl = `${backendUrl}${dbLogoUrl}`;

  const standardKeys = ["companyName", "companyEmail", "companyPhone", "companyAddress", "cin", "gst"];
  const customFields = template.TemplateIdentities?.filter((f: any) => !standardKeys.includes(f.field_key || f.key))
    .map((f: any, i: number) => ({ id: `custom_${i}_${Date.now()}`, key: f.field_key || f.key, value: f.field_value || f.value || "" })) || [];

  return {
    ...INITIAL_TEMPLATE_STATE,
    templateName: template.template_name || "",
    companyName: getIdentityValue("companyName"),
    companyEmail: getIdentityValue("companyEmail"),
    companyPhone: getIdentityValue("companyPhone"),
    companyAddress: getIdentityValue("companyAddress"),
    cin: getIdentityValue("cin"), gst: getIdentityValue("gst"),
    headerColor: headerProps.backgroundColor || "#ffffff",
    showHeaderBg: headerProps.showBackground === "true",
    showCompanyInfo: headerProps.showCompanyInfo === "true",
    logoAlignment: headerProps.logoAlignment || "left",
    logoSize: headerProps.logoSize || "medium",
    headerLayout: headerProps.headerLayout || "stacked",
    textAlignment: headerProps.textAlignment || headerProps.logoAlignment || "left",
    logoUrl: dbLogoUrl,
    showCompanyFields: safeJSON(headerProps.visibleFields, INITIAL_TEMPLATE_STATE.showCompanyFields),

    // 🔥 FIX 1: We now safely extract the saved header order from the DB
    headerFieldOrder: safeJSON(headerProps.headerFieldOrder, undefined),

    showCompanyInfoFooter: footerProps.showCompanyInfo === "true",
    showFooterFields: safeJSON(footerProps.visibleFields, INITIAL_TEMPLATE_STATE.showFooterFields),

    // 🔥 FIX 2: We now safely extract the saved footer order from the DB
    footerFieldOrder: safeJSON(footerProps.footerFieldOrder, undefined),

    decorativeLines: Number(bodyProps.decorativeLines || 1),
    lineWidth: bodyProps.lineWidth || "full", lineColor: bodyProps.lineColor || "#6B7280",
    footerColor: footerProps.backgroundColor || "#ffffff", footerText: footerProps.text || "",
    enableQR: footerProps.enableQR === "true", customFields,
  };
};

// Map DB Letter to Form State
export const parseLetterToState = (letter: any, backendUrl: string): LetterFormState => {
  console.log("IAMMMMMMMMMMMMMMKKK", letter)
  const recipient = letter.letter_recipients?.[0] || {};
  const bodyMatch = letter.body?.match(/<div class="letter-content">([\s\S]*?)<\/div>/);

  let loadedQr: any = {};
  try {
    const metaMatch = letter.body?.match(/data-qr="([^"]+)"/);
    if (metaMatch) loadedQr = JSON.parse(metaMatch[1].replace(/&quot;/g, '"'));
  } catch (e) { }

  // const sigSettings = letter.signature_setting;
  const sigSettings = letter.signature_setting || letter.SignatureSetting;
  // let signatureUrl = sigSettings?.signature_file?.file_path || sigSettings?.SignatureFile?.file_path ||
  //   (Array.isArray(sigSettings?.signature_files) ? sigSettings.signature_files[0]?.file_path : null);

  // To:
  const sigFiles = sigSettings?.signature_files || sigSettings?.SignatureFiles || [];
  const signatoryFileIds = new Set(
    (letter.signatories || letter.Signatories || [])
      .map((s: any) => s.signature_file_id || s.SignatureFile?.file_id || s.signatureFile?.file_id)
      .filter(Boolean)
  );
  const mainSigFile = sigFiles.find((f: any) => !signatoryFileIds.has(f.file_id));
  let signatureUrl = sigSettings?.signature_file?.file_path || sigSettings?.SignatureFile?.file_path || mainSigFile?.file_path || null;

  if (!signatureUrl) {
    const sigMatch = letter.body?.match(/<div class="signature">.*?<img src="([^"]+)".*?<\/div>/);
    signatureUrl = sigMatch ? sigMatch[1].replace(/&amp;/g, '&') : null;
  }
  if (signatureUrl && signatureUrl.startsWith('/uploads')) signatureUrl = `${backendUrl}${signatureUrl}`;

  const placementData = sigSettings?.signature_placement || (Array.isArray(sigSettings?.signature_placements) ? sigSettings.signature_placements[0] : {}) || {};

  return {
    ...INITIAL_LETTER_STATE,
    templateId: letter.template_id,
    recipientName: recipient.name || "", recipientEmail: recipient.email || "", recipientAddress: recipient.address || "",
    // subject: letter.subject || "Hellloooo", body: bodyMatch ? bodyMatch[1].trim() : letter.body,
    subject: "Offer Letter", body: bodyMatch ? bodyMatch[1].trim() : letter.body,
    status: letter.status || "DRAFT",
    signatureType: sigSettings?.signature_type || (signatureUrl ? "upload" : "text"),
    signaturePlacement: placementData.placement_type || "last_page",
    signaturePagePoint: placementData.page_from || "",
    // signatories: letter.signatories?.length ? letter.signatories : INITIAL_LETTER_STATE.signatories,
    signatories: letter.signatories?.length ? letter.signatories : (letter.Signatories || INITIAL_LETTER_STATE.signatories),
    uploadedSignatureUrl: signatureUrl,
    esignVerified: sigSettings?.verification_status === 'verified',
    esignSignerName: sigSettings?.verification_signer_name || "",
    esignReference: sigSettings?.verification_reference,
    esignProvider: sigSettings?.verification_provider,
    // iqCode: (letter.iquique_code && letter.iquique_code !== letter.letter_id) ? letter.iquique_code : "",
    // enableQR: loadedQr.enableQR || false, issuedBy: loadedQr.issuedBy || "",
    // issuedTo: loadedQr.issuedTo || "", subjectQr: loadedQr.subject || "",
    // created_at: (letter.status && letter.status !== "DRAFT")
    //   ? (letter.updated_at || letter.updatedAt || letter.created_at || letter.createdAt)
    //   : (letter.created_at || letter.createdAt),
    iqCode: (letter.iquique_code && letter.iquique_code !== letter.letter_id) ? letter.iquique_code : "",
    enableQR: loadedQr.enableQR !== undefined ? loadedQr.enableQR : !!letter.iquique_code,
    issuedBy: loadedQr.issuedBy || letter.Template?.TemplateIdentities?.find((f: any) => f.field_key === 'companyName' || f.key === 'companyName')?.field_value || letter.Template?.TemplateIdentities?.find((f: any) => f.field_key === 'companyName' || f.key === 'companyName')?.value || "",
    issuedTo: loadedQr.issuedTo || recipient.name || "",
    subjectQr: loadedQr.subjectQr || loadedQr.subject || letter.subject || "",
  };
};